import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import rg4js from "raygun4js";
import { useState } from "react";
import { Container, Form } from "react-bootstrap";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import {
  CommunicationLanguage,
  CommunicationStatus,
  ObjectTypeEnum,
  type ViewCommunicationItem,
} from "@/briefpoint-client";
import { useCommunicationApi, useDocumentApi } from "@/hooks/useApi";
import {
  getCommunicationsForObjectOptions,
  getDocumentOptions,
} from "@/lib/query-options";
import { normalizeError } from "@/lib/utils";
import Button from "../Button";
import Loading from "../Loading";
import { FormModal } from "../Modals";
import { toast } from "../ui/toast";
import AddEditClientTaskModal from "./AddEditClientTaskModal";
import ReviewClientTaskBox from "./ReviewClientTaskBox";

export default function ReviewClientTasks() {
  const { documentId } = useParams<{ documentId: string }>();
  const history = useHistory();
  const location = useLocation();
  const documentApi = useDocumentApi();
  const communicationApi = useCommunicationApi();
  const queryClient = useQueryClient();
  const searchParams = new URLSearchParams(location.search);
  const [deleteCommunication, setDeleteCommunication] = useState<
    ViewCommunicationItem | undefined
  >(undefined);
  const [editCommunication, setEditCommunication] = useState<
    ViewCommunicationItem | undefined
  >(undefined);

  const [language, setLanguage] = useState<CommunicationLanguage>(
    CommunicationLanguage.EN,
  );

  const documentQuery = useQuery({
    ...getDocumentOptions(documentId, documentApi),
    enabled: !!documentId,
  });

  const communicationsQuery = useQuery({
    ...getCommunicationsForObjectOptions(
      communicationApi,
      documentId,
      ObjectTypeEnum.Document,
    ),
    enabled: !!documentId,
    select: (data) => {
      return data?.find(
        (c) =>
          c.status !== CommunicationStatus.Closed &&
          c.status !== CommunicationStatus.Deleted,
      );
    },
  });

  function addTask() {
    setEditCommunication(undefined);
    searchParams.set("add_task", "true");
    history.push({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  }

  function sortUnassociatedToBottom(
    a: ViewCommunicationItem,
    b: ViewCommunicationItem,
  ) {
    // move unassociated to bottom of list
    if (a.responseInfo?.associatedObjectIds?.length === 0) {
      return 1;
    }
    if (b.responseInfo?.associatedObjectIds?.length === 0) {
      return -1;
    }
    return 0;
  }

  const deleteCommunicationMutation = useMutation({
    mutationFn: async () => {
      return communicationApi.communicationPutCommunicationItem({
        id: deleteCommunication?.id ?? "",
        putCommunicationItem: {
          id: deleteCommunication?.id ?? "",
          objectType: deleteCommunication?.objectType,
          objectId: deleteCommunication?.objectId,
          communication: deleteCommunication?.communication,
          language: deleteCommunication?.language,
          responseInfo: {
            associatedObjectIds: [],
          },
        },
      });
    },
    onSuccess: () => {
      toast.success("Task removed successfully");
    },
    onError: (error) => {
      toast.error("Failed to remove task. Please try again.");
      rg4js("send", {
        error: normalizeError(error),
      });
    },
    onSettled: () => {
      setDeleteCommunication(undefined);
      queryClient.invalidateQueries({
        queryKey: getCommunicationsForObjectOptions(
          communicationApi,
          documentId,
          ObjectTypeEnum.Document,
        ).queryKey,
      });
    },
  });

  const updateCommunicationMutation = useMutation({
    mutationFn: async (ids: number[]) => {
      return communicationApi.communicationUpdateCommunication({
        id: communicationsQuery.data?.id ?? "",

        putClientCommunication: {
          status: CommunicationStatus.Pending,
          communicationInfo: {
            ...communicationsQuery.data?.communicationInfo,
            requestedReviewIds: ids,
          },
        },
      });
    },
    onError: (error) => {
      rg4js("send", {
        error: normalizeError(error),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: getCommunicationsForObjectOptions(
          communicationApi,
          documentId,
          ObjectTypeEnum.Document,
        ).queryKey,
      });
      history.push(
        `/documents/${documentId}/client-feedback/${communicationsQuery.data?.id}/confirm-client-info`,
      );
    },
  });

  function saveAndRedirectToReviewFlow() {
    const requestedReviewIds =
      communicationsQuery.data?.externalCommunicationItems
        ?.map((i) =>
          i.responseInfo?.associatedObjectIds?.map((id) => Number(id)),
        )
        .reduce((acc, curr) => acc?.concat(curr ?? []) ?? [], []);

    updateCommunicationMutation.mutate(requestedReviewIds ?? []);
  }

  return (
    <Container className="page-container">
      <AddEditClientTaskModal
        interrogatories={documentQuery.data?.interrogatories ?? []}
        communicationId={communicationsQuery.data?.id ?? ""}
        externalCommunication={editCommunication}
      />
      <FormModal
        cancelText="No, go back"
        confirmText="Yes, I'm sure"
        show={!!deleteCommunication}
        onClose={() => setDeleteCommunication(undefined)}
        onConfirm={() => deleteCommunicationMutation.mutate()}
        cancelButtonVariant="outline-primary"
      >
        <div style={{ fontSize: "24px", fontWeight: "500" }}>
          Are you sure you want to delete
          <br />
          from all RFPs?
        </div>
      </FormModal>
      <Loading isLoading={documentQuery.isLoading}>
        <div className="bread-crumbs mb-3">
          <Link
            to={`/wizard/${documentId}/${documentQuery.data?.interrogatories.length}`}
          >
            Review
          </Link>{" "}
          {"> Client Tasks"}
        </div>
        <div className="mb-3">
          <h1 className="feedback-header">Review Client Tasks</h1>
        </div>
        <div className="fw-bold fs-4">{documentQuery.data?.name}</div>
        <div className="pt-3 pb-2">
          <div>
            <label htmlFor="select-english" className="fs-6 fw-bold mb-0">
              Client's Primary Language
            </label>
          </div>
          <div className="d-inline-flex align-items-center">
            <Form.Check
              label="English"
              type="radio"
              inline
              checked={language === CommunicationLanguage.EN}
              onChange={() => setLanguage(CommunicationLanguage.EN)}
              id="select-english"
            />
            <Form.Check
              label="Spanish"
              type="radio"
              inline
              checked={language === CommunicationLanguage.ES}
              onChange={() => setLanguage(CommunicationLanguage.ES)}
              id="select-spanish"
            />
          </div>
        </div>
        <div className="mb-4" style={{ width: "746px" }}>
          <div>
            {communicationsQuery.data?.externalCommunicationItems
              ?.sort(sortUnassociatedToBottom)
              ?.map((externalCommunication) => (
                <ReviewClientTaskBox
                  key={externalCommunication.id}
                  communication={externalCommunication}
                  interrogatories={documentQuery.data?.interrogatories ?? []}
                  setDeleteCommunication={(externalCommunication) =>
                    setDeleteCommunication(externalCommunication)
                  }
                  setEditCommunication={(communication) => {
                    setEditCommunication(communication);
                    searchParams.set("add_task", "true");
                    history.push({
                      pathname: location.pathname,
                      search: searchParams.toString(),
                    });
                  }}
                />
              ))}
            <Button variant="outline-primary" size="sm" onClick={addTask}>
              + Add Task
            </Button>
          </div>
          <div className="d-flex justify-content-end pt-2">
            <Button variant="primary" onClick={saveAndRedirectToReviewFlow}>
              Next
            </Button>
          </div>
        </div>
      </Loading>
    </Container>
  );
}
